import { useMutation } from '@tanstack/react-query';
import { BASE_API_URL } from '../consts/base-url';
import type { Project } from '../types/project';
import type { Task } from '../types/task';
import customFetch from '../utils/fetch';
import { replaceGcsUrlWithSignedUrl } from '../utils/signed-url';
export const useAssignTask = (email: string | undefined) => {
  const {
    mutate,
    reset,
    isError,
    error,
    data,
    isSuccess,
    isPending
  } = useMutation({
    mutationKey: ['assignTask', email],
    mutationFn: async () => {
      const response = await customFetch(`${BASE_API_URL}/tasks/assign/${email}`, {});
      const {
        task,
        error
      } = (await response.json() as {
        task: Task;
        error: string | undefined;
      });
      if (!response.ok) {
        throw new Error(error);
      }
      const projectResponse = await customFetch(`${BASE_API_URL}/projects/${task.projectId}`, {});
      const {
        project,
        error: projectError
      } = (await projectResponse.json() as {
        project: Project;
        error: string | undefined;
      });
      if (!projectResponse.ok) {
        throw new Error(projectError);
      }
      const taskWithSignedUrl = await replaceGcsUrlWithSignedUrl(task);
      switch (task.input.labellingType) {
        case 'IMAGE':
          {
            return {
              task: taskWithSignedUrl,
              project,
              imageGcsUrl: task.input.imageUrl
            };
          }
        case 'VIDEO':
          {
            return {
              task: {
                ...taskWithSignedUrl,
                input: {
                  ...taskWithSignedUrl.input,
                  multiVideoList: [{
                    url: task.input.videoUrl,
                    label: 'Main Video',
                    referenceUri: 'main-media-reference'
                  }]
                }
              },
              project,
              videoGcsUrl: task.input.videoUrl
            };
          }
        case 'PDF':
          {
            return {
              task: taskWithSignedUrl,
              project,
              pdfGcsUrl: task.input.pdfUrl
            };
          }
        default:
          {
            (task.input satisfies never);
            throw new Error('Invalid labeling type');
          }
      }
    }
  });
  return {
    assignTask: mutate,
    removeTask: reset,
    isError,
    error,
    data,
    isSuccess,
    isPending
  };
};